import API_CONFIG from '../../config/api.config';
import { handleApiResponse } from './api.utils';
import apiClient from './apiClient';

/**
 * @typedef {Object} SaleRecord
 * @property {string} sku - SKU of the sold item
 * @property {number} productId - Product ID
 * @property {number|null} parentVehicleId - Parent vehicle ID if applicable
 * @property {string|null} parentVehicleSku - Parent vehicle SKU if applicable
 * @property {string} details - Product details
 * @property {string} observations - Internal observations
 * @property {string} activityObservations - Activity-specific observations
 * @property {string[]} images - Array of image URLs
 * @property {string[]} oemCode - Array of OEM codes
 * @property {string} createdDate - Creation date ISO string
 * @property {string} soldDate - Sale date ISO string
 * @property {string} soldBy - Username of the seller
 * @property {string} activityType - Type of activity (e.g., "Sold", "Deleted")
 * @property {Object} component - Component information
 * @property {string} vehicleString - Vehicle description
 * @property {string} engineString - Engine description
 */

class SalesService {
  static instance = null;

  static getInstance() {
    if (!SalesService.instance) {
      SalesService.instance = new SalesService();
    }
    return SalesService.instance;
  }

  /**
   * Get sales report for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {number} skip - Number of records to skip for pagination
   * @param {number} limit - Maximum number of records to return
   * @param {number|null} activityId - Activity ID if applicable
   * @param {number|null} componentId - Component ID to filter by
   * @param {number|null} modelId - Model ID to filter by
   * @param {number|null} engineId - Engine ID to filter by
   * @param {number|null} engineCodeId - Engine code ID to filter by
   * @param {number|null} manufacturerId - Manufacturer ID to filter by
   * @param {number|null} manufacturerCategoryId - Manufacturer category ID to filter by
   * @param {string|null} oemNumber - OEM number to filter by
   * @param {string|null} sku - SKU to filter by
   * @param {string|null} vehicleSku - Vehicle SKU to filter by
   * @param {string|null} search - Search string to filter by
   * @returns {Promise<{status: string, count: number, skip: number, limit: number, execution_time_seconds: number, sales: SaleRecord[]}>}
   */
  async getSalesReport(
    startDate, 
    endDate, 
    skip = 0, 
    limit = 50, 
    activityId = null, 
    componentId = null,
    modelId = null,
    engineId = null,
    engineCodeId = null,
    manufacturerId = null,
    manufacturerCategoryId = null,
    oemNumber = null,
    sku = null,
    vehicleSku = null,
    search = null,
    reportType = 'sales'
  ) {
    const searchParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      report_type: reportType
    });

    // Only add skip and limit if activityId is not provided
    if (!activityId) {
      searchParams.append('skip', skip.toString());
      searchParams.append('limit', limit.toString());
    }

    // Add activityId if provided
    if (activityId) {
      searchParams.append('activity_id', activityId.toString());
    }

    // Add componentId if provided
    if (componentId) {
      searchParams.append('component', componentId.toString());
    }

    // Add vehicle-related filters
    if (manufacturerCategoryId) {
      searchParams.append('manufacturer_category', manufacturerCategoryId.toString());
    }

    if (manufacturerId) {
      searchParams.append('manufacturer', manufacturerId.toString());
    }

    if (modelId) {
      searchParams.append('model', modelId.toString());
    }

    if (engineId) {
      searchParams.append('engine', engineId.toString());
    }

    if (engineCodeId) {
      searchParams.append('engineCode', engineCodeId.toString());
    }

    // Add OEM number if provided
    if (oemNumber) {
      searchParams.append('oemCode', oemNumber);
    }

    // Add SKU and vehicle SKU if provided
    if (sku) {
      searchParams.append('sku', sku);
    }

    if (vehicleSku) {
      searchParams.append('vehicle_sku', vehicleSku);
    }

    // Add search parameter if provided
    if (search) {
      searchParams.append('search', search);
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.GET_SALES_REPORT}?${searchParams}`;
    const response = await apiClient.get(endpoint);
    return response;
  }

  /**
   * Get returns report for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {number} skip - Number of records to skip for pagination
   * @param {number} limit - Maximum number of records to return
   * @param {number|null} activityId - Activity ID if applicable
   * @param {number|null} componentId - Component ID to filter by
   * @param {number|null} modelId - Model ID to filter by
   * @param {number|null} engineId - Engine ID to filter by
   * @param {number|null} engineCodeId - Engine code ID to filter by
   * @param {number|null} manufacturerId - Manufacturer ID to filter by
   * @param {number|null} manufacturerCategoryId - Manufacturer category ID to filter by
   * @param {string|null} oemNumber - OEM number to filter by
   * @param {string|null} sku - SKU to filter by
   * @param {string|null} vehicleSku - Vehicle SKU to filter by
   * @param {string|null} search - Search string to filter by
   * @returns {Promise<{status: string, count: number, skip: number, limit: number, execution_time_seconds: number, sales: SaleRecord[]}>}
   */
  async getReturnsReport(
    startDate,
    endDate,
    skip = 0,
    limit = 50,
    activityId = null,
    componentId = null,
    modelId = null,
    engineId = null,
    engineCodeId = null,
    manufacturerId = null,
    manufacturerCategoryId = null,
    oemNumber = null,
    sku = null,
    vehicleSku = null,
    search = null
  ) {
    return this.getSalesReport(
      startDate,
      endDate,
      skip,
      limit,
      activityId,
      componentId,
      modelId,
      engineId,
      engineCodeId,
      manufacturerId,
      manufacturerCategoryId,
      oemNumber,
      sku,
      vehicleSku,
      search,
      'returns'
    );
  }

  /**
   * Get total count of returns records for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {number|null} componentId - Component ID to filter by
   * @param {number|null} modelId - Model ID to filter by
   * @param {number|null} engineId - Engine ID to filter by
   * @param {number|null} engineCodeId - Engine code ID to filter by
   * @param {number|null} manufacturerId - Manufacturer ID to filter by
   * @param {number|null} manufacturerCategoryId - Manufacturer category ID to filter by
   * @param {string|null} oemNumber - OEM number to filter by
   * @param {string|null} sku - SKU to filter by
   * @param {string|null} vehicleSku - Vehicle SKU to filter by
   * @param {string|null} search - Search string to filter by
   * @returns {Promise<{status: string, total: number, execution_time_seconds: number}>}
   */
  async getReturnsCount(
    startDate,
    endDate,
    componentId = null,
    modelId = null,
    engineId = null,
    engineCodeId = null,
    manufacturerId = null,
    manufacturerCategoryId = null,
    oemNumber = null,
    sku = null,
    vehicleSku = null,
    search = null
  ) {
    return this.getSalesCount(
      startDate,
      endDate,
      componentId,
      modelId,
      engineId,
      engineCodeId,
      manufacturerId,
      manufacturerCategoryId,
      oemNumber,
      sku,
      vehicleSku,
      search,
      'returns'
    );
  }

  /**
   * Get total count of sales records for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {number|null} componentId - Component ID to filter by
   * @param {number|null} modelId - Model ID to filter by
   * @param {number|null} engineId - Engine ID to filter by
   * @param {number|null} engineCodeId - Engine code ID to filter by
   * @param {number|null} manufacturerId - Manufacturer ID to filter by
   * @param {number|null} manufacturerCategoryId - Manufacturer category ID to filter by
   * @param {string|null} oemNumber - OEM number to filter by
   * @param {string|null} sku - SKU to filter by
   * @param {string|null} vehicleSku - Vehicle SKU to filter by
   * @param {string|null} search - Search string to filter by
   * @returns {Promise<{status: string, total: number, execution_time_seconds: number}>}
   */
  async getSalesCount(
    startDate, 
    endDate, 
    componentId = null,
    modelId = null,
    engineId = null,
    engineCodeId = null,
    manufacturerId = null,
    manufacturerCategoryId = null,
    oemNumber = null,
    sku = null,
    vehicleSku = null,
    search = null,
    reportType = 'sales'
  ) {
    const searchParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      count_only: 'true',
      report_type: reportType
    });

    // Add componentId if provided
    if (componentId) {
      searchParams.append('component', componentId.toString());
    }

    // Add vehicle-related filters
    if (manufacturerCategoryId) {
      searchParams.append('manufacturer_category', manufacturerCategoryId.toString());
    }

    if (manufacturerId) {
      searchParams.append('manufacturer', manufacturerId.toString());
    }

    if (modelId) {
      searchParams.append('model', modelId.toString());
    }

    if (engineId) {
      searchParams.append('engine', engineId.toString());
    }

    if (engineCodeId) {
      searchParams.append('engineCode', engineCodeId.toString());
    }

    // Add OEM number if provided
    if (oemNumber) {
      searchParams.append('oemCode', oemNumber);
    }

    // Add SKU and vehicle SKU if provided
    if (sku) {
      searchParams.append('sku', sku);
    }

    if (vehicleSku) {
      searchParams.append('vehicle_sku', vehicleSku);
    }

    // Add search parameter if provided
    if (search) {
      searchParams.append('search', search);
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.GET_SALES_REPORT}?${searchParams}`;
    const response = await apiClient.get(endpoint);
    return response;
  }

  // Track in-flight requests to prevent duplicates
  #pendingRequests = new Map();
  // Track completed idempotency keys to prevent re-submission
  #completedIdempotencyKeys = new Map();
  // Cleanup old entries every 5 minutes
  #cleanupInterval = null;

  /**
   * Add quantity to a SKU
   * @param {Object} params
   * @param {string} params.sku - SKU/barcode of the product
   * @param {string} params.location - Location string (temporaryPlace)
   * @param {number} params.warehouseAreaId - Warehouse area ID
   * @param {string} params.operation_type - Type of quantity addition (e.g., 'return', 'correction')
   * @param {string} params.comments - Additional comments or observations
   * @param {number} params.quantity - Quantity to add (defaults to 1)
   * @param {string} params.idempotencyKey - Optional unique key to prevent duplicate submissions
   * @returns {Promise<{status: string, message: string, execution_time_seconds: number}>}
   * @throws {Error} When the product is not found or server error occurs
   */
  async addQuantity({ sku, location, warehouseAreaId, operation_type, comments, quantity = 1, idempotencyKey }) {
    // Generate request signature for deduplication
    const requestSignature = `addQuantity_${sku}_${location}_${warehouseAreaId}_${operation_type}_${quantity}`;
    
    // GUARD 1: Check if this exact request is already in-flight
    if (this.#pendingRequests.has(requestSignature)) {
      console.warn(`[SalesService] Duplicate in-flight request detected for ${requestSignature}, returning existing promise`);
      return this.#pendingRequests.get(requestSignature);
    }

    // GUARD 2: Check if this idempotency key was already processed
    if (idempotencyKey && this.#completedIdempotencyKeys.has(idempotencyKey)) {
      const cachedResponse = this.#completedIdempotencyKeys.get(idempotencyKey);
      console.warn(`[SalesService] Idempotency key ${idempotencyKey} already processed, returning cached response`);
      return cachedResponse;
    }

    // GUARD 3: Check if similar request was completed recently (within 60 seconds)
    const recentKey = `recent_${requestSignature}`;
    if (this.#completedIdempotencyKeys.has(recentKey)) {
      const { timestamp, response } = this.#completedIdempotencyKeys.get(recentKey);
      if (Date.now() - timestamp < 60000) {
        console.warn(`[SalesService] Similar request completed within last 60 seconds, returning cached response`);
        return response;
      }
    }

    const searchParams = new URLSearchParams({
      sku,
      location,
      warehouse_area_id: warehouseAreaId.toString(),
      operation_type,
      comments: comments || '',
      quantity: quantity.toString()
    });

    // Include idempotency key in request if provided
    if (idempotencyKey) {
      searchParams.append('idempotency_key', idempotencyKey);
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.ADD_QUANTITY}?${searchParams}`;
    
    // Create the request promise and track it
    const requestPromise = (async () => {
      try {
        console.log(`[SalesService] Executing addQuantity for SKU ${sku} with idempotency key: ${idempotencyKey || 'none'}`);
        const response = await apiClient.post(endpoint, undefined, { maxRetries: 1, timeoutMs: 90000 });
        
        // Cache successful response
        if (response.status === 'success') {
          if (idempotencyKey) {
            this.#completedIdempotencyKeys.set(idempotencyKey, response);
          }
          // Also cache by request signature with timestamp
          this.#completedIdempotencyKeys.set(recentKey, {
            timestamp: Date.now(),
            response
          });
        }
        
        return response;
      } finally {
        // Always clean up the pending request
        this.#pendingRequests.delete(requestSignature);
      }
    })();

    // Track the in-flight request
    this.#pendingRequests.set(requestSignature, requestPromise);

    // Start cleanup interval if not running
    this.#startCleanupInterval();

    return requestPromise;
  }

  /**
   * Start interval to clean up old idempotency keys
   */
  #startCleanupInterval() {
    if (this.#cleanupInterval) return;
    
    this.#cleanupInterval = setInterval(() => {
      const now = Date.now();
      const maxAge = 5 * 60 * 1000; // 5 minutes
      
      for (const [key, value] of this.#completedIdempotencyKeys.entries()) {
        // For recent_ keys, check timestamp
        if (key.startsWith('recent_') && value.timestamp && (now - value.timestamp > maxAge)) {
          this.#completedIdempotencyKeys.delete(key);
        }
      }
    }, 60000); // Run every minute
  }

  /**
   * Sell parts using the V2 endpoint (supports multiple stock items and external items in a single request)
   * @param {Object} params - Sale parameters
   * @param {Array} [params.stock_items=[]] - Array of stock items to sell
   * @param {Array} [params.external_items=[]] - Array of external items to sell
   * @param {string} params.sale_channel - Sales channel
   * @param {string} [params.sale_currency='RON'] - Currency
   * @param {string} [params.sale_reference] - Reference for the sale
   * @param {string} params.seller - Seller identifier
   * @param {string} params.doc_type - Document type (invoice, receipt, etc.)
   * @param {string} params.client_name - Client's name
   * @param {string} [params.client_vat_code] - Client's VAT code
   * @param {boolean} [params.client_is_tax_payer=false] - Whether client is a tax payer
   * @param {string} [params.client_address] - Client's address
   * @param {string} [params.client_city] - Client's city
   * @param {string} [params.client_county] - Client's county
   * @param {string} [params.payment_series] - Payment document series
   * @param {string} params.payment_type - Type of payment
   * @param {boolean} params.is_cash - Whether payment is in cash
   * @param {boolean} [params.save_client_to_db=false] - Whether to save client to database
   * @returns {Promise<{
   *   status: string,
   *   message: string,
   *   sold_items: Array,
   *   sold_external_items: Array,
   *   documents: Object
   * }>}
   * @throws {Error} When the sale fails for any reason
   */
  async sellPartV2({
    stock_items = [],
    external_items = [],
    sale_channel,
    sale_currency = 'RON',
    sale_reference,
    seller,
    doc_type,
    client_name,
    client_vat_code,
    client_is_tax_payer = false,
    client_address,
    client_city,
    client_county,
    payment_series,
    payment_type,
    is_cash,
    save_client_to_db = false
  }) {
    const endpoint = API_CONFIG.ENDPOINTS.SELL_PART_V2;
    
    const payload = {
      stock_items,
      external_items,
      sale_channel,
      sale_currency,
      ...(sale_reference && { sale_reference }),
      seller,
      doc_type,
      client_name,
      ...(client_vat_code && { client_vat_code }),
      ...(client_is_tax_payer !== undefined && { client_is_tax_payer }),
      ...(client_address && { client_address }),
      ...(client_city && { client_city }),
      ...(client_county && { client_county }),
      ...(payment_series && { payment_series }),
      payment_type,
      is_cash,
      save_client_to_db
    };

    const response = await apiClient.post(endpoint, payload);
    return response;
  }

  async processSale(saleData) {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PROCESS_SALE}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(saleData),
    });

    return handleApiResponse(response);
  }

  async processReturn(returnData) {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PROCESS_RETURN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(returnData),
    });

    return handleApiResponse(response);
  }

  async getStatusCounts() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.GET_SALES_STATUS_COUNTS);
    return response;
  }
}

export default SalesService.getInstance(); 